/**
 * Supabase Authentication Service
 *
 * Provides authentication operations using Supabase Auth.
 *
 * @module services/auth/supabaseAuth
 */
import { AuthError, type User, type Session } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';
import { clientLogger } from '@/lib/clientLogger';

// =============================================================================
// Types
// =============================================================================

export type SupabaseAuthUser = User;
export type SupabaseSession = Session;

/**
 * Custom error class for Supabase authentication operations.
 * Provides consistent error handling with error codes.
 */
export class SupabaseAuthError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly cause?: AuthError
  ) {
    super(message);
    this.name = 'SupabaseAuthError';
  }
}

// =============================================================================
// Error Mapping
// =============================================================================

/**
 * Map Supabase auth error messages to consistent error codes.
 */
function mapAuthErrorCode(error: AuthError): string {
  const message = error.message.toLowerCase();

  // Common error mappings (case-insensitive)
  if (message.includes('invalid login credentials')) {
    return 'INVALID_CREDENTIALS';
  }
  if (message.includes('email not confirmed')) {
    return 'EMAIL_NOT_VERIFIED';
  }
  if (message.includes('user already registered')) {
    return 'EMAIL_IN_USE';
  }
  if (message.includes('password should be at least')) {
    return 'WEAK_PASSWORD';
  }
  if (message.includes('invalid email')) {
    return 'INVALID_EMAIL';
  }
  if (message.includes('rate limit') || message.includes('security purposes')) {
    return 'TOO_MANY_REQUESTS';
  }
  if (message.includes('user not found')) {
    return 'USER_NOT_FOUND';
  }

  // Use error name or code from Supabase if available
  return error.name || 'UNKNOWN_ERROR';
}

// =============================================================================
// Authentication Operations
// =============================================================================

/**
 * Sign in with email and password.
 *
 * @param email - User's email address
 * @param password - User's password
 * @returns Object containing user and session
 * @throws SupabaseAuthError on failure
 */
export async function signInWithEmail(
  email: string,
  password: string
): Promise<{ user: User; session: Session }> {
  const supabase = getSupabaseClient();
  if (!supabase) {
    throw new SupabaseAuthError('Supabase not configured', 'NOT_CONFIGURED');
  }

  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    throw new SupabaseAuthError(
      error.message,
      mapAuthErrorCode(error),
      error
    );
  }

  if (!data.user || !data.session) {
    throw new SupabaseAuthError('Sign in failed', 'SIGN_IN_FAILED');
  }

  return { user: data.user, session: data.session };
}

/**
 * Sign up with email and password.
 * Sends verification email automatically if email confirmation is enabled.
 *
 * @param email - User's email address
 * @param password - User's password
 * @returns Object containing user and session (session may be null if email confirmation required)
 * @throws SupabaseAuthError on failure
 */
export async function signUpWithEmail(
  email: string,
  password: string
): Promise<{ user: User | null; session: Session | null }> {
  const supabase = getSupabaseClient();
  if (!supabase) {
    throw new SupabaseAuthError('Supabase not configured', 'NOT_CONFIGURED');
  }

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: `${window.location.origin}/auth/callback`,
    },
  });

  if (error) {
    throw new SupabaseAuthError(
      error.message,
      mapAuthErrorCode(error),
      error
    );
  }

  return { user: data.user, session: data.session };
}

/**
 * Sign out the current user.
 *
 * @throws SupabaseAuthError on failure
 */
export async function signOut(): Promise<void> {
  const supabase = getSupabaseClient();
  if (!supabase) {
    throw new SupabaseAuthError('Supabase not configured', 'NOT_CONFIGURED');
  }

  const { error } = await supabase.auth.signOut();

  if (error) {
    throw new SupabaseAuthError(
      error.message,
      mapAuthErrorCode(error),
      error
    );
  }
}

/**
 * Send password reset email.
 *
 * @param email - User's email address
 * @throws SupabaseAuthError on failure
 */
export async function sendPasswordResetEmail(email: string): Promise<void> {
  const supabase = getSupabaseClient();
  if (!supabase) {
    throw new SupabaseAuthError('Supabase not configured', 'NOT_CONFIGURED');
  }

  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/auth/reset-password`,
  });

  if (error) {
    throw new SupabaseAuthError(
      error.message,
      mapAuthErrorCode(error),
      error
    );
  }
}

/**
 * Resend email verification.
 * Note: Supabase uses resend method for signup confirmation emails.
 *
 * @param email - User's email address
 * @throws SupabaseAuthError on failure
 */
export async function resendVerificationEmail(email: string): Promise<void> {
  const supabase = getSupabaseClient();
  if (!supabase) {
    throw new SupabaseAuthError('Supabase not configured', 'NOT_CONFIGURED');
  }

  const { error } = await supabase.auth.resend({
    type: 'signup',
    email,
    options: {
      emailRedirectTo: `${window.location.origin}/auth/callback`,
    },
  });

  if (error) {
    throw new SupabaseAuthError(
      error.message,
      mapAuthErrorCode(error),
      error
    );
  }
}

// =============================================================================
// Session & User Management
// =============================================================================

/**
 * Get current session.
 *
 * @returns Current session or null if not authenticated
 */
export async function getSession(): Promise<Session | null> {
  const supabase = getSupabaseClient();
  if (!supabase) return null;

  const { data } = await supabase.auth.getSession();
  return data.session;
}

/**
 * Get current user.
 *
 * @returns Current user or null if not authenticated
 */
export async function getCurrentUser(): Promise<User | null> {
  const supabase = getSupabaseClient();
  if (!supabase) return null;

  const { data } = await supabase.auth.getUser();
  return data.user;
}

/**
 * Update user password.
 *
 * @param newPassword - New password
 * @throws SupabaseAuthError on failure
 */
export async function updatePassword(newPassword: string): Promise<void> {
  const supabase = getSupabaseClient();
  if (!supabase) {
    throw new SupabaseAuthError('Supabase not configured', 'NOT_CONFIGURED');
  }

  const { error } = await supabase.auth.updateUser({ password: newPassword });

  if (error) {
    throw new SupabaseAuthError(
      error.message,
      mapAuthErrorCode(error),
      error
    );
  }
}

// =============================================================================
// Auth State Subscriptions
// =============================================================================

/**
 * Auth state change event types from Supabase.
 */
export type SupabaseAuthEvent =
  | 'INITIAL_SESSION'
  | 'SIGNED_IN'
  | 'SIGNED_OUT'
  | 'TOKEN_REFRESHED'
  | 'USER_UPDATED'
  | 'PASSWORD_RECOVERY';

/**
 * Subscribe to auth state changes.
 *
 * @param callback - Function called when auth state changes
 * @returns Unsubscribe function to stop listening
 */
export function onAuthStateChange(
  callback: (event: string, session: Session | null) => void
): () => void {
  const supabase = getSupabaseClient();
  if (!supabase) {
    return () => {};
  }

  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session);
  });

  return () => {
    data.subscription.unsubscribe();
  };
}

// =============================================================================
// Token Management
// =============================================================================

/**
 * Get the current access token.
 * Useful for making authenticated API calls.
 *
 * @returns Access token or null if not authenticated
 */
export async function getAccessToken(): Promise<string | null> {
  const session = await getSession();
  return session?.access_token ?? null;
}

/**
 * Refresh the current session.
 *
 * @returns Refreshed session or null
 */
export async function refreshSession(): Promise<Session | null> {
  const supabase = getSupabaseClient();
  if (!supabase) return null;

  const { data, error } = await supabase.auth.refreshSession();

  if (error) {
    clientLogger.error('Failed to refresh session:', error.message);
    return null;
  }

  return data.session;
}
